import React, { useState } from 'react';
import { ArrowRight, Shield, Database, Lock, FileText, CheckCircle, Key, HardDrive } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const DataProtectionPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const dataProtectionSolutions = [
    {
      icon: Lock,
      title: "Data Encryption & Key Management",
      description: "Advanced encryption solutions with centralized key management for data at rest and in transit.",
      features: ["AES-256 encryption", "Key lifecycle management", "Hardware security modules", "Certificate management"]
    },
    {
      icon: Database,
      title: "Database Security",
      description: "Comprehensive database protection with access controls, monitoring, and threat detection.",
      features: ["Database encryption", "Access monitoring", "Privilege management", "Audit logging"]
    },
    {
      icon: FileText,
      title: "Data Loss Prevention",
      description: "Prevent unauthorized data exfiltration with content inspection and policy enforcement.",
      features: ["Content classification", "Policy enforcement", "Endpoint protection", "Email security"]
    },
    {
      icon: HardDrive,
      title: "Backup & Recovery",
      description: "Secure backup solutions with encryption, versioning, and rapid recovery capabilities.",
      features: ["Encrypted backups", "Point-in-time recovery", "Disaster recovery", "Compliance reporting"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 text-slate-100' 
          : 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1181298/pexels-photo-1181298.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Data Protection"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-amber-950/90' 
              : 'bg-gradient-to-r from-amber-50/90 to-orange-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Database className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-amber-400' : 'text-amber-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Data <span className={isDarkMode ? "text-amber-400" : "text-amber-600"}>Protection</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Comprehensive data protection solutions including encryption, backup, 
              data loss prevention, and compliance management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-amber-600 hover:bg-amber-700 text-white hover:shadow-amber-500/25" 
                    : "bg-amber-600 hover:bg-amber-700 text-white hover:shadow-amber-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Data Security Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-slate-900" 
                    : "border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
                }`}
              >
                View Data Flow
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Data Protection Solutions Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Advanced Data Protection Solutions
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Protect your most valuable asset - your data - with enterprise-grade security
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {dataProtectionSolutions.map((solution, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <solution.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-amber-400' : 'text-amber-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{solution.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default DataProtectionPage;